"use client"
import {
  CarModel,
  CarModelSpecialStrip,
  HomePage as HomePageData,
} from "@/api/generated/BMWInterfaces"
import { Desktop, Mobile, useIsDesktop } from "@/lib/common/MediaQueries"
import { sortClick } from "@/lib/gtm"
import colors from "@/lib/theme/colors"
import { theme } from "@/lib/theme/theme"
import styled from "@emotion/styled"
import { Box, Button, FormControl, InputBase, MenuItem, Select, Typography } from "@mui/material"
import Image from "next/image"
import { SetStateAction, useEffect, useRef, useState } from "react"
import CarsCard from "../shared/CarsCard"
import CustomBreadcrumbs from "../shared/CustomBreadcrumbs"
import MostClickedCars from "../shared/MostClickedCars"
import NotFoundCars from "../shared/NotFoundCars"
import BenefitStrip from "./BenefitStrip"
import SearchIntro from "./SearchIntro"

const NavigationRow = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`

const MobileNavigationColumn = styled.div`
  display: none;
  ${theme.breakpoints.down("md")} {
    display: flex;
    flex-direction: column;
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
  }
`

const StickyFilterBar = styled.div<{ isStuck: boolean }>`
  display: none;
  ${theme.breakpoints.down("md")} {
    display: flex;
    flex-direction: column;
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
    position: sticky;
    top: 60px;
    z-index: 10;
    background-color: white;
    box-shadow: ${({ isStuck }) => (isStuck ? "0 4px 25px 0 rgba(0, 0, 0, 0.15)" : "none")};
    transition: box-shadow 0.2s ease;
  }
`

const MobileNavigationRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`
const StyledFilterButtonsWrapper = styled.div`
  display: flex;
  border-top: 1px solid ${colors.directVeryLightGray};
  border-bottom: 1px solid ${colors.directVeryLightGray};

  width: 100%;
  align-items: center;
  height: 44px;

  /* :not(style) - Emotion SSR inlines <style> tags here; display: flex would show their CSS */
  & > :not(style) {
    border-radius: 0;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  & > :last-child {
    border-right: 1px solid ${colors.directVeryLightGray};
  }
`
const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${theme.breakpoints.up("md")} {
    overflow: hidden;
    gap: 24px;
  }
`

const ResultContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  ${theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const ResultRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  ${theme.breakpoints.down("md")} {
    padding-top: 40px;
    justify-content: center;
  }
`

const StyledMenuItem = styled(MenuItem)`
  border: none;
  background-color: transparent;
  padding: 8px 8px 8px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--Direct-Disable, #f9f9f9);
  gap: 24px;
  &.Mui-selected {
    background-color: transparent;
    /* display: none; */
  }
`

const BootstrapInput = styled(InputBase)(({}) => ({
  "label + &": {
    marginTop: 3,
  },
  "& .MuiInputBase-input": {
    borderRadius: 4,
    position: "relative",
    backgroundColor: "transparent",
    border: "none", // Remove border
    fontSize: 16,
    padding: "10px 26px 10px 12px",
    [theme.breakpoints.down("md")]: {
      padding: "8px 0px 6px 8px",
    },
    "&:focus": {
      borderRadius: 4,
      borderColor: "transparent", // Remove focus border color
      boxShadow: "none", // Remove focus box shadow
    },
  },
}))

const OuterLayout = styled.div``

const StyledFilterButton = styled(Button)`
  font-size: 16px;
  color: ${colors.directBlack};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0px;
  min-width: unset;
  position: relative;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: right;
  vertical-align: middle;
  text-decoration-style: solid;
  text-decoration-thickness: 1px;
  text-underline-offset: 2.3px;
  align-items: center;
  height: 24px;
`

const FilterBadge = styled.div`
  position: relative;
  top: -6px;
  margin-left: -4px;
  background-color: ${colors.directAzure};
  color: white;
  border-radius: 50%;
  width: 8px;
  height: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  border: 1px solid ${colors.white};
`

const ActiveFiltersContainer = styled(Box)`
  display: flex;
  gap: 10px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${theme.breakpoints.down("md")} {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`

const FilterChip = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: ${colors.directBlack};
  white-space: nowrap;
  flex-shrink: 0;
`

const RemoveFilterButton = styled(Button)`
  min-width: unset;
  padding: 0;
  width: 8.5px;
  padding-top: 2px;
  height: 10.99px;
  background-color: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

const GridCars = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 272px);
  justify-content: center;
  gap: 22px;
  width: fit-content;
  margin: 0 auto;
  ${theme.breakpoints.down("md")} {
    justify-content: center;
    gap: 16px;
    display: block;
    width: 100%;
    max-width: 332px;
  }
  ${theme.breakpoints.up("xl")} {
    grid-template-columns: repeat(4, 272px);
  }
`

const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  ${theme.breakpoints.up("md")} {
    gap: 8px;
  }
`

const MobileIntro = styled.div`
  margin-top: 8px;
`

const StyledSentinel = styled.div`
  display: none;
  ${theme.breakpoints.down("md")} {
    display: block;
    height: 1px;
    width: 100%;
    pointer-events: none;
  }
`

const IconComponent = (): React.ReactElement => {
  return (
    <Image
      style={{ alignSelf: "center" }}
      src="/assets/select.svg"
      alt="בחירת מיון"
      width={12}
      height={12}
    />
  )
}

type Props = {
  minMaxPrice: number[]
  brandChoose: string
  openMobileFilters: () => void
  homeData: HomePageData
  results: CarModel[]
  filterHighToLow: "high" | "low"
  setFilterHighToLow: (value: SetStateAction<"high" | "low">) => void
  description?: string
  title?: string
  intro?: string | null
  isLoading: boolean
  activeFiltersCount: number
  chosenBrands?: Array<{ id: string; name: string }>
  chosenEngineTypes?: Array<{ id: string; name: string }>
  chosenVehicleTypes?: Array<{ value: string; label: string }>
  onRemoveBrand?: (id: string) => void
  onRemoveEngineType?: (id: string) => void
  onRemoveVehicleType?: (value: string) => void
  onRemovePriceFilter?: () => void
  hasPriceFilter?: boolean
  activeBenefits?: CarModelSpecialStrip[]
  showBenefitTimer?: boolean
  isBenefitPage?: boolean
}

export const SearchContent = ({
  openMobileFilters,
  filterHighToLow,
  results,
  setFilterHighToLow,
  description,
  title,
  intro,
  isLoading,
  activeFiltersCount,
  chosenBrands,
  chosenEngineTypes,
  chosenVehicleTypes,
  onRemoveBrand,
  onRemoveEngineType,
  onRemoveVehicleType,
  onRemovePriceFilter,
  hasPriceFilter,
  activeBenefits,
  showBenefitTimer,
  isBenefitPage = false,
}: Props): React.ReactElement => {
  const isDesktop = useIsDesktop()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting)
      },
      { threshold: 0, rootMargin: "-60px 0px 0px 0px" },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  return (
    <FilterContent>
      <MobileNavigationColumn>
        <CustomBreadcrumbs
          current={{ title: "לכל ההצעות", url: "/search" }}
          parents={[{ title: "ראשי", url: "/" }]}
        />
        <MobileNavigationRow>
          {!isBenefitPage && (
            <Typography variant="h3" component={isDesktop ? "div" : "h1"}>
              {title || "מה יהיה הרכב הבא שלך?"}
            </Typography>
          )}
          <Desktop>
            <StyledFilterButton style={{ textDecoration: "underline" }} onClick={openMobileFilters}>
              <Image src="/assets/filter.svg" alt="filter" width={16} height={16} />
              סינון לפי
              {activeFiltersCount > 0 && <FilterBadge />}
            </StyledFilterButton>
          </Desktop>
        </MobileNavigationRow>
        {description && (
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: description || "" }}
            suppressHydrationWarning
            sx={{ marginTop: "16px", wordBreak: "break-word" }}
          />
        )}
        {!isBenefitPage && intro && (
          <MobileIntro>
            <SearchIntro html={intro} variant="body2" />
          </MobileIntro>
        )}
      </MobileNavigationColumn>
      <StyledSentinel ref={sentinelRef} />
      <StickyFilterBar isStuck={isStuck}>
        {!isDesktop && (
          <StyledFilterButtonsWrapper>
            <StyledFilterButton onClick={openMobileFilters}>
              <Image src="/assets/filter.svg" alt="filter" width={16} height={16} />
              סנן לפי
              {activeFiltersCount > 0 && <FilterBadge />}
            </StyledFilterButton>

            <FormControl variant="standard">
              <Select
                labelId="demo-customized-select-label"
                id="demo-customized-select"
                value={filterHighToLow}
                onChange={(e) => {
                  setFilterHighToLow(e.target.value as "low" | "high")
                  sortClick()
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        borderRadius: 0,
                        "& .MuiList-root": {
                          padding: 0,
                          backgroundColor: "white",
                          boxShadow: "0px 5px 15px 0px rgba(0, 36, 78, 0.21) !important",
                          paddingLeft: "8px",
                          paddingRight: "8px",
                        },
                      },
                    },
                  },
                }}
                IconComponent={() => <IconComponent />}
                input={<BootstrapInput />}
                renderValue={(selected) => (
                  <>
                    <Mobile>
                      <Typography variant="body2">
                        {selected === "high" ? "המחיר הגבוה לנמוך" : "המחיר הנמוך לגבוה"}
                      </Typography>
                    </Mobile>
                  </>
                )}
              >
                <StyledMenuItem value={"low"}>
                  <Typography variant="body3">מהמחיר הנמוך לגבוה</Typography>
                  {filterHighToLow === "low" && (
                    <Image alt="checked" src={"/images/check.png"} width={16} height={16} />
                  )}
                </StyledMenuItem>
                <StyledMenuItem value={"high"}>
                  <Typography variant="body3">מהמחיר הגבוה לנמוך</Typography>
                  {filterHighToLow === "high" && (
                    <Image alt="checked" src={"/images/check.png"} width={16} height={16} />
                  )}
                </StyledMenuItem>
              </Select>
            </FormControl>
          </StyledFilterButtonsWrapper>
        )}
        {activeFiltersCount > 0 && (
          <ActiveFiltersContainer>
            {chosenBrands?.map((brand) => (
              <FilterChip key={brand.id}>
                <Typography variant="body2" color={colors.white}>
                  {brand.name}
                </Typography>
                <RemoveFilterButton onClick={() => onRemoveBrand?.(brand.id)}>
                  <Image src="/assets/white-x.svg" alt="remove" layout="fill" />
                </RemoveFilterButton>
              </FilterChip>
            ))}
            {chosenEngineTypes?.map((engineType) => (
              <FilterChip key={engineType.id}>
                <Typography variant="body2" color={colors.white}>
                  {engineType.name}
                </Typography>
                <RemoveFilterButton onClick={() => onRemoveEngineType?.(engineType.id)}>
                  <Image src="/assets/white-x.svg" alt="remove" layout="fill" />
                </RemoveFilterButton>
              </FilterChip>
            ))}
            {chosenVehicleTypes?.map((vehicleType) => (
              <FilterChip key={vehicleType.value}>
                <Typography variant="body2" color={colors.white}>
                  {vehicleType.label}
                </Typography>
                <RemoveFilterButton onClick={() => onRemoveVehicleType?.(vehicleType.value)}>
                  <Image src="/assets/white-x.svg" alt="remove" layout="fill" />
                </RemoveFilterButton>
              </FilterChip>
            ))}
            {hasPriceFilter && (
              <FilterChip>
                <Typography variant="body2" color={colors.white}>
                  טווח מחירים
                </Typography>
                <RemoveFilterButton onClick={onRemovePriceFilter}>
                  <Image src="/assets/white-x.svg" alt="remove" layout="fill" />
                </RemoveFilterButton>
              </FilterChip>
            )}
          </ActiveFiltersContainer>
        )}
      </StickyFilterBar>
      {!isBenefitPage && (
        <Desktop>
          <TopBar>
            <NavigationRow>
              <Typography variant="h1" component={isDesktop ? "h1" : "div"}>
                {title || "מה יהיה הרכב הבא שלך?"}
              </Typography>
            </NavigationRow>
            {description && (
              <Typography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: description }}
                suppressHydrationWarning
                sx={{ "& p": { margin: 0, wordBreak: "break-word" } }}
              />
            )}
            {intro && <SearchIntro html={intro} variant="body1" />}
          </TopBar>
        </Desktop>
      )}
      <ResultContent>
        <ResultRow>
          <Desktop>
            <Typography variant="h3">
              מצאנו <strong>{results.length}</strong> הצעות מיוחדות
            </Typography>
          </Desktop>
          <Mobile>
            <Typography variant="body1">
              מצאנו <strong>{results.length}</strong> הצעות מיוחדות
            </Typography>
          </Mobile>
          <Desktop>
            <FormControl variant="standard">
              <Select
                labelId="demo-customized-select-label"
                id="demo-customized-select"
                value={filterHighToLow}
                onChange={(e) => {
                  setFilterHighToLow(e.target.value as "low" | "high")
                  sortClick()
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        borderRadius: 0,
                        "& .MuiList-root": {
                          padding: 0,
                          backgroundColor: "white",
                          boxShadow: "0px 5px 15px 0px rgba(0, 36, 78, 0.21) !important",
                          paddingLeft: "8px",
                          paddingRight: "8px",
                        },
                      },
                    },
                  },
                }}
                IconComponent={() => <IconComponent />}
                input={<BootstrapInput />}
                renderValue={(selected) => (
                  <Desktop>
                    <Typography variant="body2">
                      {selected === "high" ? "המחיר הגבוה לנמוך" : "המחיר הנמוך לגבוה"}
                    </Typography>
                  </Desktop>
                )}
              >
                <StyledMenuItem value={"low"}>
                  <Typography variant="body3">מהמחיר הנמוך לגבוה</Typography>
                  {filterHighToLow === "low" && (
                    <Image alt="checked" src={"/images/check.png"} width={16} height={16} />
                  )}
                </StyledMenuItem>
                <StyledMenuItem value={"high"}>
                  <Typography variant="body3">מהמחיר הגבוה לנמוך</Typography>
                  {filterHighToLow === "high" && (
                    <Image alt="checked" src={"/images/check.png"} width={16} height={16} />
                  )}
                </StyledMenuItem>
              </Select>
            </FormControl>
          </Desktop>
        </ResultRow>
        {activeBenefits?.map((benefit) => (
          <BenefitStrip key={benefit.id} data={benefit} showBenefitTimer={showBenefitTimer} />
        ))}
        <OuterLayout>
          {results.length === 0 ? (
            <>
              <NotFoundCars />
              <MostClickedCars />
            </>
          ) : (
            <GridCars>
              {isLoading
                ? Array.from({ length: 6 }).map((_, index) => <CarsCard key={index} loading />)
                : results.map((car, index) => <CarsCard key={index} data={car} h2={true} />)}
            </GridCars>
          )}
        </OuterLayout>
      </ResultContent>
    </FilterContent>
  )
}
